import React, { useState } from "react";
import BlockRenderer from "../components/canvas/BlockRenderer";
import { Badge } from "../components/index";

// ---- Types ----

interface BlockData {
  blockId: string;
  type: string;
  version: number;
  payload: Record<string, unknown>;
  renderHints?: Record<string, string>;
  provenance?: {
    toolName?: string;
    runId?: string;
    sourceUrl?: string;
  };
  actions?: Array<{
    actionId: string;
    label: string;
    type: string;
    confirmRequired: boolean;
  }>;
}

export interface InlineArtifactData {
  artifactId: string;
  title: string;
  kind: string;
  status: "draft" | "review" | "approved" | "archived";
  blocks: BlockData[];
  sessionId?: string;
  createdAt: string;
}

interface InlineArtifactProps {
  artifact: InlineArtifactData;
  onOpenFull?: (artifactId: string) => void;
  onBlockUpdate?: (artifactId: string, blockId: string, payload: Record<string, unknown>) => void;
  onBlockApprove?: (artifactId: string, blockId: string, notes?: string) => void;
  onBlockReject?: (artifactId: string, blockId: string, notes?: string) => void;
}

// ---- Status helpers ----

const STATUS_STYLES: Record<
  string,
  { variant: "blue" | "amber" | "green" | "default"; label: string }
> = {
  draft: { variant: "default", label: "Draft" },
  review: { variant: "amber", label: "Review" },
  approved: { variant: "green", label: "Approved" },
  archived: { variant: "default", label: "Archived" },
};

const KIND_LABELS: Record<string, string> = {
  research: "Research",
  draft: "Draft",
  plan: "Plan",
  analysis: "Analysis",
  report: "Report",
};

// ---- Component ----

export default function InlineArtifact({
  artifact,
  onOpenFull,
  onBlockUpdate,
  onBlockApprove,
  onBlockReject,
}: InlineArtifactProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const statusInfo = STATUS_STYLES[artifact.status] ?? STATUS_STYLES.draft;
  const kindLabel = KIND_LABELS[artifact.kind] ?? artifact.kind;
  const hasMultipleBlocks = artifact.blocks.length > 1;

  return (
    <div
      className="rounded-xl overflow-hidden my-2"
      style={{
        border: "1px solid var(--c-border)",
        backgroundColor: "var(--c-surface)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2.5"
        style={{
          backgroundColor: "var(--c-surface-2)",
          borderBottom: "1px solid var(--c-border)",
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {/* Artifact icon */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="shrink-0"
            style={{ color: "var(--c-primary)" }}
          >
            <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
            <path d="M5 4.5h4M5 7h4M5 9.5h2" />
          </svg>
          <span className="text-sm font-medium truncate" style={{ color: "var(--c-text)" }}>
            {artifact.title}
          </span>
          <span className="text-[10px] shrink-0" style={{ color: "var(--c-text-faint)" }}>
            {kindLabel}
          </span>
          <Badge variant={statusInfo.variant} size="sm">
            {statusInfo.label}
          </Badge>
        </div>

        <div className="flex items-center gap-1 shrink-0 ml-2">
          {/* Expand/collapse toggle */}
          {hasMultipleBlocks && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-[10px] px-2 py-1 rounded transition-colors"
              style={{ color: "var(--c-text-faint)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
            >
              {expanded ? "Collapse" : `${artifact.blocks.length} blocks`}
            </button>
          )}

          {/* Open full button */}
          <button
            onClick={() => onOpenFull?.(artifact.artifactId)}
            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors"
            style={{ color: "var(--c-primary)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "var(--c-primary-subtle)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M6 1h3v3M4 9H1V6M9 1L5.5 4.5M1 9l3.5-3.5" />
            </svg>
            Open full
          </button>
        </div>
      </div>

      {/* Block content (compact mode shows first block only, limited height) */}
      <div
        className="relative"
        style={{
          maxHeight: expanded ? undefined : 240,
          overflow: expanded ? undefined : "hidden",
        }}
      >
        <div className="p-3 space-y-3">
          {(expanded ? artifact.blocks : artifact.blocks.slice(0, 1)).map((block) => (
            <BlockRenderer
              key={block.blockId}
              block={block}
              interactive={artifact.status === "review"}
              onBlockUpdate={
                onBlockUpdate
                  ? (blockId, payload) => onBlockUpdate(artifact.artifactId, blockId, payload)
                  : undefined
              }
              onBlockApprove={
                onBlockApprove
                  ? (blockId, notes) => onBlockApprove(artifact.artifactId, blockId, notes)
                  : undefined
              }
              onBlockReject={
                onBlockReject
                  ? (blockId, notes) => onBlockReject(artifact.artifactId, blockId, notes)
                  : undefined
              }
            />
          ))}
        </div>

        {/* Gradient fade for overflow in compact mode */}
        {!expanded && artifact.blocks.length > 0 && (
          <div
            className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none"
            style={{
              background: "linear-gradient(transparent, var(--c-surface))",
            }}
          />
        )}
      </div>

      {/* Footer with block count and timestamp */}
      <div
        className="flex items-center justify-between px-4 py-2"
        style={{
          borderTop: "1px solid var(--c-border)",
          backgroundColor: "var(--c-surface-2)",
        }}
      >
        <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
          {artifact.blocks.length} block{artifact.blocks.length !== 1 ? "s" : ""}
          {artifact.blocks.length > 1 && !expanded && " — click expand to see all"}
        </span>
        <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
          {new Date(artifact.createdAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>
    </div>
  );
}
